import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CountryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const CountryMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<CountryMaster | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    phoneCode: '',
    currency: '',
    locale: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'countries', includeInactive: true });
      const data = (result.data as any).data as CountryMaster[];
      setCountries(data);
    } catch (error: any) {
      onError(error.message || '国マスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (country?: CountryMaster) => {
    if (country) {
      setEditingCountry(country);
      setFormData({
        code: country.code,
        nameJa: country.name.ja,
        nameTh: country.name.th || '',
        nameEn: country.name.en || '',
        phoneCode: country.phoneCode,
        currency: country.currency,
        locale: country.locale,
        sortOrder: country.sortOrder,
        isActive: country.isActive,
      });
    } else {
      setEditingCountry(null);
      setFormData({
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        phoneCode: '',
        currency: '',
        locale: '',
        sortOrder: countries.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCountry(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.code || !formData.nameJa || !formData.phoneCode || !formData.currency) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingCountry) {
        // 更新
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingCountry.id,
          type: 'countries',
          data: {
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            phoneCode: formData.phoneCode,
            currency: formData.currency,
            locale: formData.locale,
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('国マスタを更新しました');
      } else {
        // 新規作成
        const createCountry = httpsCallable(functions, 'createCountry');
        await createCountry({
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          phoneCode: formData.phoneCode,
          currency: formData.currency,
          locale: formData.locale,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('国マスタを作成しました');
      }

      handleCloseDialog();
      fetchCountries();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (country: CountryMaster) => {
    if (!window.confirm(`${country.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      await deleteMasterData({
        id: country.id,
        type: 'countries',
      });
      onSuccess('国マスタを削除しました');
      fetchCountries();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          国を追加
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>電話コード</TableCell>
              <TableCell>通貨</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {countries.map((country) => (
              <TableRow key={country.id}>
                <TableCell>{country.code}</TableCell>
                <TableCell>{country.name.ja}</TableCell>
                <TableCell>{country.name.th || '-'}</TableCell>
                <TableCell>{country.name.en || '-'}</TableCell>
                <TableCell>{country.phoneCode}</TableCell>
                <TableCell>{country.currency}</TableCell>
                <TableCell>{country.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={country.isActive ? '有効' : '無効'}
                    color={country.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(country)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(country)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCountry ? '国マスタを編集' : '国マスタを追加'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingCountry}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="電話コード"
            value={formData.phoneCode}
            onChange={(e) => setFormData({ ...formData, phoneCode: e.target.value })}
            margin="normal"
            required
            placeholder="+81"
          />
          <TextField
            fullWidth
            label="通貨"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            margin="normal"
            required
            placeholder="JPY"
          />
          <TextField
            fullWidth
            label="ロケール"
            value={formData.locale}
            onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
            margin="normal"
            placeholder="ja-JP"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CountryMasterTab;
